import React, { useContext } from 'react';
import cx from 'classnames';

import { UserContext } from 'components/Providers/Providers';
import { MediaQueryContext } from '@arrive/responsive-provider';

import { isValidInput, TruckAndDriverTextEntry } from 'components/BookingModal/TruckAndDriver/TruckAndDriverEntry';
import { CountryCodeDropdown } from 'components/CountrySuggestionsDropdown/CountrySuggestionsDropdown';

import { TRUCK_DRIVER_CHANGE_KEYS } from 'constants/truckDriverConstants';

import { LoadTrackingTypeIds } from 'sharedTypes/loads/loadTracking';
import { DriverInfo } from 'sharedTypes/loads/loadType';

import styles from './BookingModal.scss'

interface Props {
  sectionIsActive: boolean;
  newDriverInfo: DriverInfo;
  setNewDriverInfo: (driverInfo: DriverInfo) => void;
  isTeamLoad: boolean;
  isTruckAndDriverEdit?: boolean;
  isTrailerNumRequiredCust?: boolean;
  isSameDay?: boolean;
}

const getTrackingName = (id: LoadTrackingTypeIds, useMP: boolean) => {
  if (!useMP) {
    if (id === LoadTrackingTypeIds.TruckerTools) {
      return "Trucker Tools"
    } else if (id === LoadTrackingTypeIds.Manual) {
      return "Manual"
    } else if (id === LoadTrackingTypeIds.Project44) {
      return "Project44"
    }
  }
  return "MacroPoint"
}

export default function BookingModalTruckAndDriverEntry({ sectionIsActive, newDriverInfo, setNewDriverInfo, isTeamLoad, isTruckAndDriverEdit, isTrailerNumRequiredCust, isSameDay = false }: Props) {
  const currentUser = useContext(UserContext)
  const currentMediaTypes = useContext(MediaQueryContext);

  const updateNewDriverInfo = (newValue: string | number, key: string) => {
    if (typeof newValue === 'string') {
      const trimmedValue = newValue.trimStart();
      if (isValidInput(trimmedValue, key)) {
        setNewDriverInfo({
          ...newDriverInfo,
          [key]: trimmedValue
        });
      }
    } else {
      setNewDriverInfo({
        ...newDriverInfo,
        [key]: newValue
      });
    }
  };
  const trackingVendor = currentUser?.LoadTrackingVendorId;
  const isPreferedTracking = trackingVendor === LoadTrackingTypeIds.MacroPoint || trackingVendor === LoadTrackingTypeIds.Project44 || currentUser?.UseMP;
  const isMP = trackingVendor === LoadTrackingTypeIds.MacroPoint || currentUser?.UseMP;
  const trackingInfoString = 'Collecting this now will enable us to start tracking without having to reach out to you directly.'

  if(!isSameDay && isTrailerNumRequiredCust){
    return (
      <div className={cx(styles.listContent, !sectionIsActive && styles.disabledContent)}>
        <div className={styles.bookingTruckDriverContainer}>
          <TruckAndDriverTextEntry
            required={isTrailerNumRequiredCust}
            disabled={!sectionIsActive && !newDriverInfo.TrailerNumber}
            classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
            label={'Trailer Number'}
            onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.TRAILER_NUMBER)}
            value={newDriverInfo.TrailerNumber}
            theme='ethanol'
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cx(styles.listContent, !sectionIsActive && styles.disabledContent)}>
      <div className={styles.header}>Who&apos;s picking up the load?</div>
      <div className={styles.subHeader}>
        {trackingInfoString}
        {isPreferedTracking && <> Please contact your rep if you <em>do not</em> wish to use {getTrackingName(trackingVendor, currentUser?.UseMP)} for tracking.</>}
      </div>
      <div className={styles.bookingTruckDriverContainer}>
        {!currentMediaTypes.xs &&
          <>
            <div className={styles.bookingTruckDriverColumn}>
              <TruckAndDriverTextEntry
                classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                disabled={!sectionIsActive}
                label={isTeamLoad ? 'Driver 1 Name' : 'Driver Name'}
                onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.DRIVER_NAME)}
                value={newDriverInfo.Driver}
                theme='ethanol'
              />
              {isTeamLoad &&
                <TruckAndDriverTextEntry
                  disabled={!sectionIsActive}
                  classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                  label={'Driver 2 Name'}
                  onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.SECOND_DRIVER_NAME)}
                  value={newDriverInfo.SecondDriver}
                  theme='ethanol'
                />
              }
              {!!(isPreferedTracking || isTruckAndDriverEdit) &&
                <TruckAndDriverTextEntry
                  disabled={!sectionIsActive}
                  classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                  label={'Truck Number'}
                  onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.TRUCK_NUMBER)}
                  value={newDriverInfo.TruckNumber}
                  theme='ethanol'
                />
              }
            </div>
            <div className={cx(styles.bookingTruckDriverColumn, styles.bookingTruckDriverColumnWide)}>
              <div className={styles.bookingTruckDriverCountryRow}>
                <CountryCodeDropdown
                  disabled={!sectionIsActive}
                  label={'Ctry'}
                  value={newDriverInfo.DriverPhoneCountryId}
                  onChange={(countryId) => updateNewDriverInfo(countryId, TRUCK_DRIVER_CHANGE_KEYS.DRIVER_PHONE_COUNTRY_ID)}
                  required={!!newDriverInfo.DriverPhone}
                />
                <TruckAndDriverTextEntry
                  disabled={!sectionIsActive}
                  classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                  label={isTeamLoad ? 'Driver 1 Phone' : 'Driver Phone'}
                  onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.DRIVER_PHONE_NUMBER)}
                  type={'tel'}
                  value={newDriverInfo.DriverPhone}
                  theme='ethanol'
                  phoneCode={null}
                />
              </div>
              {isTeamLoad && (
                <div className={styles.bookingTruckDriverCountryRow}>
                  <CountryCodeDropdown
                    disabled={!sectionIsActive}
                    label={'Ctry'}
                    value={newDriverInfo.SecondDriverPhoneCountryId}
                    onChange={(countryId) => updateNewDriverInfo(countryId, TRUCK_DRIVER_CHANGE_KEYS.SECOND_DRIVER_PHONE_COUNTRY_ID)}
                    required={!!newDriverInfo.SecondDriverPhoneNumber}
                  />
                  <TruckAndDriverTextEntry
                    disabled={!sectionIsActive}
                    classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                    label={'Driver 2 Phone'}
                    onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.SECOND_DRIVER_PHONE_NUMBER)}
                    type={'tel'}
                    value={newDriverInfo.SecondDriverPhoneNumber}
                    theme='ethanol'
                    phoneCode={null}
                  />
                </div>
              )
              }
              {!!(isMP || isTruckAndDriverEdit || isTrailerNumRequiredCust) &&
                <TruckAndDriverTextEntry
                  required={isTrailerNumRequiredCust}
                  disabled={!sectionIsActive}
                  classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                  label={'Trailer Number'}
                  onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.TRAILER_NUMBER)}
                  value={newDriverInfo.TrailerNumber}
                  theme='ethanol'
                />
              }
            </div>
          </>
        }
        {currentMediaTypes.xs &&
          <>
            <TruckAndDriverTextEntry
              classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
              disabled={!sectionIsActive}
              label={isTeamLoad ? 'Driver 1 Name' : 'Driver Name'}
              onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.DRIVER_NAME)}
              value={newDriverInfo.Driver}
              theme='ethanol'
            />
            <CountryCodeDropdown
              disabled={!sectionIsActive}
              label={'Country Code'}
              value={newDriverInfo.DriverPhoneCountryId}
              onChange={(countryId) => updateNewDriverInfo(countryId, TRUCK_DRIVER_CHANGE_KEYS.DRIVER_PHONE_COUNTRY_ID)}
              selectableListPopoverClasses={{ anchorClass: styles.countrySuggestionsDropdownContainerMobile }}
              required={!!newDriverInfo.DriverPhone}
              matchAnchorWidth
            />
            <TruckAndDriverTextEntry
              disabled={!sectionIsActive}
              classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
              label={isTeamLoad ? 'Driver 1 Phone' : 'Driver Phone'}
              onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.DRIVER_PHONE_NUMBER)}
              type={'tel'}
              value={newDriverInfo.DriverPhone}
              theme='ethanol'
              phoneCode={null}
            />
            {isTeamLoad &&
              <>
                <TruckAndDriverTextEntry
                  disabled={!sectionIsActive}
                  classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                  label={'Driver 2 Name'}
                  onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.SECOND_DRIVER_NAME)}
                  value={newDriverInfo.SecondDriver}
                  theme='ethanol'
                />
                <CountryCodeDropdown
                  disabled={!sectionIsActive}
                  label={'Country Code'}
                  value={newDriverInfo.SecondDriverPhoneCountryId}
                  onChange={(countryId) => updateNewDriverInfo(countryId, TRUCK_DRIVER_CHANGE_KEYS.SECOND_DRIVER_PHONE_COUNTRY_ID)}
                  selectableListPopoverClasses={{ anchorClass: styles.countrySuggestionsDropdownContainerMobile }}
                  required={!!newDriverInfo.SecondDriverPhoneNumber}
                  matchAnchorWidth
                />
                <TruckAndDriverTextEntry
                  disabled={!sectionIsActive}
                  classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                  label={'Driver 2 Phone'}
                  onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.SECOND_DRIVER_PHONE_NUMBER)}
                  type={'tel'}
                  value={newDriverInfo.SecondDriverPhoneNumber}
                  theme='ethanol'
                  phoneCode={null}
                />
              </>
            }
            {!!(isPreferedTracking || isTruckAndDriverEdit) &&
              <TruckAndDriverTextEntry
                disabled={!sectionIsActive}
                classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                label={'Truck Number'}
                onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.TRUCK_NUMBER)}
                value={newDriverInfo.TruckNumber}
                theme='ethanol'
              />
            }
            {!!(isMP || isTruckAndDriverEdit || isTrailerNumRequiredCust) &&
              <TruckAndDriverTextEntry
                required={isTrailerNumRequiredCust}
                disabled={!sectionIsActive}
                classes={{ inputWrapper: styles.bookingTruckDriverTextEntryOverwrite }}
                label={'Trailer Number'}
                onChange={(event) => updateNewDriverInfo(event.target.value, TRUCK_DRIVER_CHANGE_KEYS.TRAILER_NUMBER)}
                value={newDriverInfo.TrailerNumber}
                theme='ethanol'
              />
            }
          </>
        }
      </div>
    </div>
  )
}
